import { useLocation, Link } from "wouter";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { Building2, MapPin, Calendar, Bell, User, LayoutDashboard } from "lucide-react";
import { Header } from "./Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { cn } from "../../lib/utils";
import type { ReactNode } from "react";

const TABS = [
  { label: "Overview", href: "/studio", icon: LayoutDashboard },
  { label: "Events", href: "/studio/events", icon: Calendar },
  { label: "Notices", href: "/studio/notices", icon: Bell },
  { label: "Profile", href: "/studio/profile", icon: User },
] as const;

interface StudioLayoutProps {
  children: ReactNode;
}

export function StudioLayout({ children }: StudioLayoutProps) {
  const [location] = useLocation();
  const { user } = useCurrentUser();

  // For the pending badge on the Events tab
  const events = useQuery(api.events.getAllEventsAtOwnedVenues);
  const pendingCount = events?.filter((e) => e.status === "pending").length ?? 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 pt-6 pb-10">
        {/* Studio identity header */}
        <header className="mb-6">
          <h1 className="font-display text-2xl text-foreground flex items-center gap-2.5">
            <Building2 className="w-6 h-6 text-primary flex-shrink-0" />
            {user?.studioName || "My Studio"}
          </h1>
          {user?.studioAddress && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              {user.studioAddress}
            </p>
          )}
        </header>

        {/* Tab bar */}
        <div className="flex gap-0.5 border-b border-border mb-8 -mx-1 overflow-x-auto scrollbar-none">
          {TABS.map(({ label, href, icon: Icon }) => {
            const isActive =
              href === "/studio"
                ? location === "/studio"
                : location.startsWith(href);

            return (
              <Link
                key={href}
                href={href}
                className={cn(
                  "relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors rounded-t",
                  isActive
                    ? "text-foreground border-b-2 border-primary -mb-px"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
                {label === "Events" && pendingCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-amber-500 text-white rounded-full leading-none">
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {children}
      </main>
    </div>
  );
}
